import React, { createContext, useContext, useReducer } from 'react';

/**
 * @typedef {Object} Attendee
 * @property {string} id
 * @property {string} husbandName
 * @property {string} wifeName
 * @property {string} address
 * @property {string} venue
 * @property {number} currentStep
 * @property {string=} completedDate
 * @property {boolean} certificateGenerated
 * @property {'BBS1'|'BBS2'|'BBS3'} bbsLevel
 * @property {string} joinDate
 * @property {string} speakerName
 */

/**
 * @typedef {Object} Seminar
 * @property {string} id
 * @property {string} title
 * @property {string} speakerName
 * @property {string} organizationName
 * @property {string=} logoUrl
 * @property {string=} startDate
 * @property {string=} endDate
 * @property {string=} description
 */

/**
 * @typedef {Object} AppState
 * @property {Attendee[]} attendees
 * @property {Seminar} seminar
 */

/**
 * @typedef {Object} AddAttendeeAction
 * @property {'ADD_ATTENDEE'} type
 * @property {Omit<Attendee, 'id'>} payload
 */

/**
 * @typedef {Object} UpdateAttendeeAction
 * @property {'UPDATE_ATTENDEE'} type
 * @property {Attendee} payload
 */

/**
 * @typedef {Object} DeleteAttendeeAction
 * @property {'DELETE_ATTENDEE'} type
 * @property {string} payload
 */

/**
 * @typedef {Object} UpdateAttendeeStepAction
 * @property {'UPDATE_ATTENDEE_STEP'} type
 * @property {{ id: string, step: number, completed?: boolean }} payload
 */

/**
 * @typedef {Object} UpdateSeminarAction
 * @property {'UPDATE_SEMINAR'} type
 * @property {Partial<Seminar>} payload
 */

/**
 * @typedef {AddAttendeeAction | UpdateAttendeeAction | DeleteAttendeeAction | UpdateAttendeeStepAction | UpdateSeminarAction} AppAction
 */

function appReducer(state, action) {
  switch (action.type) {
    case 'ADD_ATTENDEE':
      return {
        ...state,
        attendees: [
          ...state.attendees,
          {
            ...action.payload,
            id: Date.now().toString(),
          },
        ],
      };
    case 'UPDATE_ATTENDEE':
  return {
    ...state,
    attendees: state.attendees.map((attendee) =>
      attendee.id === action.payload.id
        ? { ...action.payload }
        : attendee
    ),
  };
case 'DELETE_ATTENDEE':
  return {
    ...state,
    attendees: state.attendees.filter((attendee) => attendee.id !== action.payload),
  };
case 'UPDATE_ATTENDEE_STEP':
  return {
    ...state,
    attendees: state.attendees.map((attendee) =>
      attendee.id === action.payload.id
        ? {
            ...attendee,
            currentStep: action.payload.step,
            completedDate: action.payload.completed ? new Date().toISOString().split('T')[0] : attendee.completedDate,
            certificateGenerated: action.payload.step === 3 && action.payload.completed ? true : attendee.certificateGenerated,
          }
        : attendee
    ),
  };
    case 'UPDATE_SEMINAR':
      return {
        ...state,
        seminar: {
          ...state.seminar,
          ...action.payload,
        },
      };
    case 'SET_ATTENDEES':
      return {
        ...state,
        attendees: action.payload,
      };
    default:
      return state;
  }
}

const AppContext = createContext(null);

export function AppProvider({ children }) {
  const [state, dispatch] = useReducer(appReducer, {
    attendees: [],
    seminar: {},
    user: null,
  });

  // NOTE: Dashboard fetches its own attendee data via api.js.
  // The context reducer remains available for local state management
  // (e.g., optimistic UI updates) but no longer duplicates the fetch.

  return (
    <AppContext.Provider value={{ state, dispatch }}>
      {children}
    </AppContext.Provider>
  );
}

export function useApp() {
  const context = useContext(AppContext);
  if (!context) {
    throw new Error('useApp must be used within an AppProvider');
  }
  return context;
}